// Fuel consumption, logistics resupply, and one-shot fuel/ammo/culmination warnings. See spec §4
// "Logistics" and plan Task 10. Ammo consumption from firing is handled in combat.ts (which runs
// earlier in the tick, per Task 12's `tick()` order: movement -> detection -> combat -> logistics); this
// module only consumes fuel (from `movedCm`, which movement.ts sets each tick and this module resets to
// 0 after consuming) and runs resupply plus the warning events once per sim minute.
import type { Dials, Side, Unit, UnitDef, UnitType } from './types';
import { TICKS_PER_MIN } from './types';
import type { EventLog } from './events';
import { loadRules, type Rules } from './rules';
// See combat.ts's UNIT_DEFS comment: deck.ts (Task 6) does not yet export UNIT_DEFS, so this module
// reads data/units.json directly, same as combat.ts, scenario.ts, and detection.ts.
// eslint-disable-next-line @typescript-eslint/no-var-requires
import unitsJson from '../../data/units.json';

const UNIT_DEFS: Record<UnitType, UnitDef> = unitsJson as unknown as Record<UnitType, UnitDef>;

const DEFAULT_RULES = loadRules('realistic');

/**
 * Per-Sim one-shot warning trackers, mirroring combat.ts's CombatState: held on the owning Sim (via
 * `newLogisticsState`) rather than as module-level state, so two Sims (or a replay alongside a live run)
 * in the same process never share or collide on these one-shot flags. `fuelLow`/`ammoLow` are keyed by
 * unit id and never cleared (a unit that recovers above 20% does not get a second warning if it drops
 * again - "once" per the plan); `forceFuelLow` and `culminated` are keyed by side (culmination is
 * defined for blue/side 0 only, but a side either is or isn't in the set) and likewise never reset
 * within a run.
 */
export interface LogisticsState {
  fuelLow: Set<number>;
  ammoLow: Set<number>;
  forceFuelLow: Set<Side>;
  culminated: Set<Side>;
}

/** A fresh, empty LogisticsState for a new Sim (or a replay run independent of any live Sim). */
export function newLogisticsState(): LogisticsState {
  return { fuelLow: new Set(), ammoLow: new Set(), forceFuelLow: new Set(), culminated: new Set() };
}

function distCm(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Advances fuel consumption and (once per sim minute) logistics resupply and the one-shot low-fuel /
 * low-ammo / force-fuel-low / culmination warnings. Runs every tick per the plan's Task 10 interface
 * comment ("runs each tick; warnings at minute boundaries"): fuel consumption happens unconditionally so
 * `movedCm` is drained and reset every tick, while the warning events are only evaluated at minute
 * boundaries (t % TICKS_PER_MIN === 0), matching resupply's own per-minute cadence. `state` (see
 * `LogisticsState`/`newLogisticsState`) holds this Sim's one-shot warning flags; pass the same instance
 * across ticks of one Sim, and a fresh one per Sim (a live run, a replay, or a test).
 */
export function stepLogistics(
  t: number, units: Unit[], dials: Dials, log: EventLog, state: LogisticsState, rules: Rules = DEFAULT_RULES,
): void {
  void dials; // reserved: no dial currently changes logistics rates; kept for interface parity with stepCombat/stepDetection.
  const lg = rules.logistics;
  const ordered = [...units].sort((a, b) => a.id - b.id);

  // 1. Fuel consumption from this tick's movement (movement.ts sets movedCm; reset it here so the next
  // tick starts fresh regardless of whether this unit is alive or moved at all).
  for (const u of ordered) {
    if (u.alive && u.movedCm > 0) {
      const def = UNIT_DEFS[u.type];
      const km = u.movedCm / 100000; // cm -> km
      u.fuel = Math.max(0, u.fuel - def.fuelPerKm * km);
    }
    u.movedCm = 0;
  }

  if (t % TICKS_PER_MIN !== 0) return;

  // 2. Logistics resupply: +resupplyFuelPerMin fuel, +resupplyAmmoPerMin ammo per minute to alive
  // friendlies (not the log unit itself) within resupplyRadiusM, capped at 100.
  const resupplyRadiusCm = lg.resupplyRadiusM * 100;
  for (const logUnit of ordered) {
    if (!logUnit.alive || logUnit.type !== 'log') continue;
    for (const u of ordered) {
      if (u.id === logUnit.id || !u.alive || u.side !== logUnit.side) continue;
      if (distCm(logUnit, u) <= resupplyRadiusCm) {
        u.fuel = Math.min(100, u.fuel + lg.resupplyFuelPerMin);
        u.ammo = Math.min(100, u.ammo + lg.resupplyAmmoPerMin);
      }
    }
  }

  // 3. Per-unit one-shot low-fuel / low-ammo warnings.
  for (const u of ordered) {
    if (!u.alive) continue;
    if (u.fuel < lg.fuelLowThreshold && !state.fuelLow.has(u.id)) {
      state.fuelLow.add(u.id);
      log.push(t, 'unit.fuelLow', u.side, { fuel: u.fuel }, { unitId: u.id });
    }
    if (u.ammo < lg.ammoLowThreshold && !state.ammoLow.has(u.id)) {
      state.ammoLow.add(u.id);
      log.push(t, 'unit.ammoLow', u.side, { ammo: u.ammo }, { unitId: u.id });
    }
  }

  // 4. Force-wide one-shot fuel warning per side: average fuel of alive ground (non-uas) units below threshold.
  for (const side of [0, 1] as const) {
    if (state.forceFuelLow.has(side)) continue;
    const ground = ordered.filter((u) => u.alive && u.side === side && u.type !== 'uas');
    if (ground.length === 0) continue;
    const avgFuel = ground.reduce((sum, u) => sum + u.fuel, 0) / ground.length;
    if (avgFuel < lg.forceFuelLowThreshold) {
      state.forceFuelLow.add(side);
      log.push(t, 'force.fuelLow', side, { avgFuel }, { significant: true });
    }
  }

  // 5. Culmination, blue (side 0) only, one-shot: average strength of alive blue ground combat units
  // (excluding uas, log, hq - ISR/sustain/command elements don't measure combat culmination) below threshold.
  if (!state.culminated.has(0)) {
    const combatGround = ordered.filter(
      (u) => u.alive && u.side === 0 && u.type !== 'uas' && u.type !== 'log' && u.type !== 'hq',
    );
    if (combatGround.length > 0) {
      const avgStrength = combatGround.reduce((sum, u) => sum + u.strength, 0) / combatGround.length;
      if (avgStrength < lg.culminationThreshold) {
        state.culminated.add(0);
        log.push(t, 'culminated', 0, { avgStrength }, { significant: true });
      }
    }
  }
}
